import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Plus, 
  Search, 
  User, 
  Trash2, 
  Edit2, 
  UserPlus, 
  UserMinus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Zap,
  Wallet
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import usePageTitle from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RFIDCards = () => {
  usePageTitle('Kartat RFID');
  
  const { token, user } = useAuth();
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Form state
  const [newCard, setNewCard] = useState({ rfid_tag: '', label: '', user_id: '' });
  const [assignUserId, setAssignUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCards();
    fetchUsers();
  }, [pagination.page, statusFilter]);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(search && { search })
      });
      
      const response = await fetch(`${API_URL}/api/rfid-cards?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch cards');
      
      const data = await response.json();
      setCards(data.cards || []);
      setPagination(prev => ({
        ...prev,
        total: data.total,
        pages: data.pages
      }));
    } catch (error) {
      console.error('Error fetching cards:', error);
      toast.error('Gabim në ngarkimin e kartave');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      if (user?.role === 'BUSINESS') {
        const response = await fetch(`${API_URL}/api/v1/business/employees`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const employees = Array.isArray(data?.employees) ? data.employees : [];
          const mappedUsers = employees.map((e) => ({
            id: e.employee_account_id || e.employee_id,
            full_name: e.employee_name,
            email: e.employee_email
          })).filter(u => u.id);
          setUsers(mappedUsers);
        }
      } else {
        const response = await fetch(`${API_URL}/api/clients?limit=500`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.items || data.clients || []);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchCards();
  };

  const handleCreateCard = async () => {
    if (!newCard.rfid_tag) {
      toast.error('Shkruani kodin RFID');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/rfid-cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCard)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create card');
      }
      
      toast.success('Karta RFID u krijua me sukses');
      setCreateDialogOpen(false);
      setNewCard({ rfid_tag: '', label: '', user_id: '' });
      fetchCards();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignCard = async () => {
    if (!selectedCard || !assignUserId) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/rfid-cards/${selectedCard.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: assignUserId })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to assign card');
      }
      
      toast.success('Karta u caktua me sukses');
      setAssignDialogOpen(false);
      setSelectedCard(null);
      setAssignUserId('');
      fetchCards();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignCard = async (card) => {
    try {
      const response = await fetch(`${API_URL}/api/rfid-cards/${card.id}/unassign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to unassign card');
      
      toast.success('Karta u hoq nga përdoruesi');
      fetchCards();
    } catch (error) {
      toast.error('Gabim në heqjen e kartës');
    }
  };

  const handleDeleteCard = async () => {
    if (!selectedCard) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/rfid-cards/${selectedCard.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete card');
      
      toast.success('Karta u fshi');
      setDeleteDialogOpen(false);
      setSelectedCard(null);
      fetchCards();
    } catch (error) {
      toast.error('Gabim në fshirjen e kartës');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (card) => {
    const newStatus = card.status === 'active' ? 'inactive' : 'active';
    
    try {
      const response = await fetch(`${API_URL}/api/rfid-cards/${card.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update card');
      
      toast.success(`Karta u ${newStatus === 'active' ? 'aktivizua' : 'çaktivizua'}`);
      fetchCards();
    } catch (error) {
      toast.error('Gabim në përditësimin e kartës');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Aktive</Badge>;
      case 'inactive':
        return <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">Joaktive</Badge>;
      case 'blocked':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Bllokuar</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Use shared date formatter for consistent European format
  const formatRFIDDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  };

  return (
    <div className="space-y-6" data-testid="rfid-cards-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-outfit">Kartat RFID</h1>
          <p className="text-zinc-400 mt-1">Menaxhoni kartat RFID për autentifikimin e klientëve</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} data-testid="add-card-btn">
          <Plus className="w-4 h-4 mr-2" />
          Shto Kartë
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Totali Kartave</p>
                <p className="text-2xl font-bold text-white mt-1">{pagination.total}</p>
              </div>
              <CreditCard className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Aktive</p>
                <p className="text-2xl font-bold text-green-400 mt-1">
                  {cards.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Të Caktuara</p>
                <p className="text-2xl font-bold text-blue-400 mt-1">
                  {cards.filter(c => c.user_id).length}
                </p>
              </div>
              <User className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-400">Pa Caktuar</p>
                <p className="text-2xl font-bold text-amber-400 mt-1">
                  {cards.filter(c => !c.user_id).length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Kërko sipas kodit RFID ose emrit..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700">
                <SelectValue placeholder="Statusi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të gjitha</SelectItem>
                <SelectItem value="active">Aktive</SelectItem>
                <SelectItem value="inactive">Joaktive</SelectItem>
                <SelectItem value="blocked">Bllokuar</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="secondary">
              <Search className="w-4 h-4 mr-2" />
              Kërko
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Cards Table */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-outfit">Lista e Kartave</CardTitle>
          <CardDescription>Të gjitha kartat RFID të regjistruara në sistem</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">Nuk ka karta RFID</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Shto Kartën e Parë
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-400">Kodi RFID</TableHead>
                  <TableHead className="text-zinc-400">Emri</TableHead>
                  <TableHead className="text-zinc-400">Klienti</TableHead>
                  <TableHead className="text-zinc-400">Balanca</TableHead>
                  <TableHead className="text-zinc-400">Statusi</TableHead>
                  <TableHead className="text-zinc-400">Karikuesi i Fundit</TableHead>
                  <TableHead className="text-zinc-400">Përdorime</TableHead>
                  <TableHead className="text-zinc-400">Përdorur</TableHead>
                  <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cards.map((card) => (
                  <TableRow key={card.id} className="border-zinc-800">
                    <TableCell>
                      <code className="px-2 py-1 bg-zinc-800 rounded text-sm font-mono text-blue-400">
                        {card.rfid_tag}
                      </code>
                    </TableCell>
                    <TableCell className="text-white">{card.label || '-'}</TableCell>
                    <TableCell>
                      {card.user ? (
                        <div>
                          <p className="text-white font-medium">{card.user.full_name}</p>
                          <p className="text-xs text-zinc-500">{card.user.email}</p>
                        </div>
                      ) : (
                        <span className="text-zinc-500 italic">Pa caktuar</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {card.wallet_balance !== null && card.wallet_balance !== undefined ? (
                        <span className={`font-medium ${card.wallet_balance >= 100 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(card.wallet_balance, 'Kredite')}
                        </span>
                      ) : (
                        <span className="text-zinc-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(card.status)}</TableCell>
                    <TableCell>
                      {card.last_charger ? (
                        <div>
                          <p className="text-white text-sm">{card.last_charger.name || card.last_charger.charger_id}</p>
                          {card.last_charger.location && typeof card.last_charger.location === 'string' && (
                            <p className="text-xs text-zinc-500">{card.last_charger.location}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-zinc-500 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-400">{card.usage_count || 0}</TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {card.last_used_at ? formatRFIDDate(card.last_used_at) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {card.user_id ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnassignCard(card)}
                            className="text-amber-400 hover:text-amber-300"
                            title="Hiq nga klienti"
                          >
                            <UserMinus className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedCard(card);
                              setAssignDialogOpen(true);
                            }}
                            className="text-blue-400 hover:text-blue-300"
                            title="Cakto te klienti"
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleStatus(card)}
                          className={card.status === 'active' ? 'text-zinc-400' : 'text-green-400'}
                          title={card.status === 'active' ? 'Çaktivizo' : 'Aktivizo'}
                        >
                          {card.status === 'active' ? (
                            <XCircle className="w-4 h-4" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedCard(card);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-400 hover:text-red-300"
                          title="Fshi"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
              <p className="text-sm text-zinc-500">
                Faqja {pagination.page} nga {pagination.pages} ({pagination.total} karta)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Para
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Pas
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Card Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">Shto Kartë RFID</DialogTitle>
            <DialogDescription>
              Regjistroni një kartë të re RFID në sistem
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Kodi RFID *</Label>
              <Input
                placeholder="psh. A1B2C3D4E5F6"
                value={newCard.rfid_tag}
                onChange={(e) => setNewCard({ ...newCard, rfid_tag: e.target.value.toUpperCase() })}
                className="bg-zinc-800 border-zinc-700 font-mono"
                data-testid="rfid-tag-input"
              />
              <p className="text-xs text-zinc-500">Kodi unik i kartës RFID (hex ose numerik)</p>
            </div>
            <div className="space-y-2">
              <Label>Emri / Etiketa</Label>
              <Input
                placeholder="psh. Kartë Blu #1"
                value={newCard.label}
                onChange={(e) => setNewCard({ ...newCard, label: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Cakto te Klienti (opsional)</Label>
              <Select 
                value={newCard.user_id || "none"} 
                onValueChange={(val) => setNewCard({ ...newCard, user_id: val === "none" ? "" : val })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Zgjidh klientin..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                  <SelectItem value="none">Pa caktuar</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Anulo
            </Button>
            <Button onClick={handleCreateCard} disabled={submitting || !newCard.rfid_tag}>
              {submitting ? 'Duke ruajtur...' : 'Ruaj Kartën'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Card Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">Cakto Kartën</DialogTitle>
            <DialogDescription>
              Caktoni kartën <code className="text-blue-400">{selectedCard?.rfid_tag}</code> te një klient
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Zgjidh Klientin</Label>
            <Select value={assignUserId} onValueChange={setAssignUserId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-2">
                <SelectValue placeholder="Zgjidh klientin..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 max-h-60">
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    <div className="flex items-center gap-2">
                      <span>{user.full_name}</span>
                      <span className="text-xs text-zinc-500">({user.email})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Anulo
            </Button>
            <Button onClick={handleAssignCard} disabled={submitting || !assignUserId}>
              {submitting ? 'Duke caktuar...' : 'Cakto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">Fshi Kartën</DialogTitle>
            <DialogDescription>
              Jeni i sigurt që dëshironi të fshini kartën <code className="text-red-400">{selectedCard?.rfid_tag}</code>?
              Ky veprim nuk mund të zhbëhet.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteCard}
              disabled={submitting}
            >
              {submitting ? 'Duke fshirë...' : 'Fshi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RFIDCards;
